<template>
  <div
    v-if="getmessagesForQRCode.length > 0"
    class="bg-surface ma-0 information-popup-container"
  >
    <v-container
      fluid
      class="pl-0 pr-0 information-popup-header-container ma-0 pa-0"
    >
      <v-row
        class="information-popup-header ma-0"
        :class="minimisePopUp ? 'bg-popup-secondary' : 'bg-popup-primary'"
      >
        <v-col cols="2" class="pr-0 information-popup-icon-col">
          <v-badge
            v-if="hasMultipleMessages"
            color="error"
            :content="getmessagesForQRCode.length"
          >
            <v-icon
              class="information-popup-icon-plain"
              size="large"
              role="button"
              tabindex="0"
              :aria-label="t('messages.goToMessages')"
              @click="goToMessages"
              @keydown.enter="goToMessages"
              >mdi-email</v-icon
            >
          </v-badge>
          <v-icon
            v-else
            class="information-popup-icon"
            :class="minimisePopUp ? 'bg-popup-primary' : ''"
            size="large"
            >mdi-exclamation</v-icon
          >
        </v-col>
        <v-col cols="8" class="px-0">
          <h2 class="information-popup-title" :aria-label="messageTitle">
            {{ t('messages.message') }}
          </h2>
        </v-col>
        <v-col cols="2" class="pl-0 information-popup-action">
          <v-btn
            v-if="!hideButton"
            icon
            size="v-small"
            :ripple="false"
            :disabled="showCountdown"
            :aria-label="buttonAriaLabel"
            :role="showCountdown ? 'timer' : undefined"
            :aria-live="showCountdown ? 'polite' : undefined"
            @click="handleMinimiseToggleClick"
            :class="[
              'information-popup-toggle-btn',
              minimisePopUp
                ? 'minimise-button-minimised'
                : 'minimise-button-maximised',
            ]"
          >
            <span
              v-if="showCountdown"
              class="information-popup-countdown-number"
              >{{ disabledOutTimer }}</span
            >
            <v-icon v-else>{{
              minimisePopUp ? "mdi-chevron-up" : "mdi-close"
            }}</v-icon>
          </v-btn>
        </v-col> </v-row
    ></v-container>

    <div :hidden="minimisePopUp" class="information-popup-messages">
      <stop-messages
        v-if="!minimisePopUp"
        :latestMessageOnly="latestMessageOnly"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import StopMessages from "./StopMessages.vue";
import { messagesToSpeak } from "../utils/textToSpeechUtils";
import { MessageLocation } from "../../constants";
import { useI18n } from 'vue-i18n';

const PopupMode = Object.freeze({
  NORMAL: "normal",
  MINIMISED: "minimised",
  EXPANDED: "expanded",
  LOCKED: "locked",
});

export default {
  setup() {
    const { t } = useI18n();
    return { t };
  },
  components: {
    StopMessages,
  },
  props: {
    latestMessageOnly: {
      type: Boolean,
      required: false,
      default: false,
    },

    messageType: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      popupMode: PopupMode.NORMAL,

      disabledOutTimer: 0,
      myDisableTimer: null,

      userHasToggledButton: false,
    };
  },
  methods: {
    ...mapActions([
      "updatePopupMinimised",
      "updateCurrentPage",
      "updateDismissCountdownShownForMessageId",
      "updateLastDisplayedMessageId",
    ]),
    goToMessages() {
      this.updateCurrentPage("messages");
    },
    handleMinimiseToggleClick() {
      this.userHasToggledButton = true;
      const minimising = this.popupMode !== PopupMode.MINIMISED;
      this.popupMode = minimising ? PopupMode.MINIMISED : PopupMode.NORMAL;
      this.updatePopupMinimised(minimising);
    },
    startDisableTimer() {
      this.disabledOutTimer = parseInt(
        process.env.VUE_APP_MESSAGE_POPUP_DISABLE_BUTTON_TIMER,
        10
      );
      this.myDisableTimer = setInterval(() => {
        this.disabledOutTimer--;
        if (this.disabledOutTimer <= 0) {
          clearInterval(this.myDisableTimer);
          this.myDisableTimer = null;
          if (this.popupMode === PopupMode.EXPANDED) {
            this.popupMode = PopupMode.NORMAL;
          }
        }
      }, 1000);
    },
    resetPopupState() {
      if (this.myDisableTimer) {
        clearInterval(this.myDisableTimer);
        this.myDisableTimer = null;
      }
      this.disabledOutTimer = 0;
      this.popupMode = PopupMode.NORMAL;
    },
    nonDismissableMessageAction() {
      this.popupMode = PopupMode.LOCKED;
    },
    expandableMessageAction(forceMaximise) {
      if (this.popupMinimised && !forceMaximise) {
        this.popupMode = PopupMode.MINIMISED;
        return;
      }

      // Footer messages are always immediately dismissable — no countdown.
      if (this.messageType == MessageLocation.FOOTER) {
        this.popupMode = PopupMode.NORMAL;
        return;
      }

      const currentMessageId = this.getHighestPriorityMessage
        ? this.getHighestPriorityMessage.id
        : null;

      const countdownAlreadyShown =
        currentMessageId != null &&
        this.dismissCountdownShownForMessageId === currentMessageId;

      // Only run the countdown when a genuinely new message is shown for the
      // first time. Re-evaluations for the same message (data refresh,
      // navigation, manual maximise) must keep the dismiss button enabled —
      // otherwise it flickers disabled -> enabled.
      if (forceMaximise && !countdownAlreadyShown) {
        this.popupMode = PopupMode.EXPANDED;
        this.startDisableTimer();
        if (currentMessageId != null) {
          this.updateDismissCountdownShownForMessageId(currentMessageId);
        }
      } else {
        this.popupMode = PopupMode.NORMAL;
      }
    },
    setupUIBasedOnMessages() {
      this.resetPopupState();

      const currentMessageId = this.getHighestPriorityMessage
        ? this.getHighestPriorityMessage.id
        : null;
      const messageIdChanged = currentMessageId !== this.lastDisplayedMessageId;
      this.updateLastDisplayedMessageId(currentMessageId);

      const forceMaximise =
        messageIdChanged && this.messageType != MessageLocation.FOOTER;

      if (forceMaximise) {
        this.updatePopupMinimised(false);
      }

      if (
        this.messageType == MessageLocation.EMERGENCY ||
        this.messageType == MessageLocation.FULL_SCREEN_STATIC
      ) {
        this.nonDismissableMessageAction();
      } else {
        this.expandableMessageAction(forceMaximise);
      }
    },
  },
  mounted() {
    this.setupUIBasedOnMessages();
  },
  computed: {
    ...mapGetters(["getmessagesForQRCode", "getHighestPriorityMessage"]),
    ...mapState([
      "popupMinimised",
      "dismissCountdownShownForMessageId",
      "lastDisplayedMessageId",
    ]),
    minimisePopUp() {
      return this.popupMode === PopupMode.MINIMISED;
    },
    hideButton() {
      return this.popupMode === PopupMode.LOCKED;
    },
    buttonAriaLabel() {
      if (this.showCountdown) {
        return this.t('messages.buttonAvailableIn', { seconds: this.disabledOutTimer });
      }
      return this.minimisePopUp
        ? this.t('messages.openMessageButton')
        : this.t('messages.closeMessageButton');
    },
    showCountdown() {
      if (
        this.messageType !== MessageLocation.EMERGENCY &&
        this.userHasToggledButton
      ) {
        return false;
      }
      return (
        this.popupMode === PopupMode.EXPANDED && this.disabledOutTimer > 0
      );
    },
    hasMultipleMessages() {
      return this.getmessagesForQRCode.length > 1;
    },
    messageTitle() {
      let title = this.t('messages.message') + "! ";
      if (this.getmessagesForQRCode.length > 0) {
        title =
          title +
          messagesToSpeak(this.getmessagesForQRCode, this.latestMessageOnly, this.t);
      }
      return title;
    },
  },
  watch: {
    getmessagesForQRCode() {
      this.setupUIBasedOnMessages();
    },
  },
};
</script>

<style>

.v-btn.v-btn--disabled {
  opacity: 0.75 !important;
}

.message-card {
  margin: 10px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #fff;

  font-size: 16px !important;

  text-align: left;
  transition: all 0.3s;
  background: rgb(var(--v-theme-error));
}

.message-card span {
  font-size: 20px !important;
}

/* The toggle button is clicked to minimise/maximise. Vuetify's ripple +
   active/hover/focus overlay briefly darkens it, which reads as a "disabled"
   flash. Suppress the overlay so the button never dims on interaction. */
.information-popup-toggle-btn .v-btn__overlay {
  opacity: 0 !important;
}

.minimise-button-minimised {
  border: 2px solid !important;
  border-color: rgb(var(--v-theme-surface-variant-2)) !important;
  color: rgb(var(--v-theme-surface-variant-2)) !important;
  background-color: transparent !important;
}

.minimise-button-maximised {
  border: 2px solid !important;
  color: var(--v-my-color) !important;
  border-color: white !important;
  background-color: transparent !important;
}

.information-popup-container {
  display: flex;
  flex-direction: column;
}

.information-popup-header-container {
  flex-shrink: 0;
}

.information-popup-messages {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.information-popup-header {
  margin-top: 0px !important;
  margin-bottom: 0px !important;
  width: 100%;
  max-width: 100% !important;
  -moz-border-radius: 0px;
  -webkit-border-radius: 5px 5px 0px 0px;
  border-radius: 5px 5px 0px 0px;
}
.information-popup-icon {
  margin-bottom: 5px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  color: black;
  background: white;
}
.information-popup-icon-plain {
  margin-bottom: 5px;
}
.information-popup-title {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  margin: 0;
}
.information-popup-icon-col {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 20px !important;
}
.information-popup-icon-col .information-popup-icon,
.information-popup-icon-col .information-popup-icon-plain {
  margin-bottom: 0;
}
.information-popup-action {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 20px !important;
}
.information-popup-toggle-btn {
  width: 35px !important;
  height: 35px !important;
  min-width: 35px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  transition: none !important;
}
.information-popup-countdown-number {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
</style>